import { collection, addDoc, serverTimestamp, getDocs, query, orderBy, doc, updateDoc } from 'firebase/firestore';
import { ref, uploadString, getDownloadURL } from 'firebase/storage';
import { toJpeg } from 'html-to-image';
import { toast } from 'sonner';
import { db, storage, auth } from '../firebase';
import { handleFirestoreError, OperationType } from '../utils/firestoreErrorHandler';

export type FeedbackType = 'suggestion' | 'problem';
export type FeedbackStatus = 'new' | 'reviewing' | 'planned' | 'done' | 'discarded';
export type FeedbackPriority = 'low' | 'normal' | 'high' | 'critical';

export interface FeedbackReport {
  id?: string;
  type: FeedbackType;
  status: FeedbackStatus;
  priority: FeedbackPriority;
  message: string;
  screenshotUrl?: string;
  hasScreenshot: boolean;
  route: string;
  userId: string;
  userName: string;
  userEmail: string;
  appVersion: string;
  platform: string;
  os: string;
  browser: string;
  viewportWidth: number;
  viewportHeight: number;
  adminNotes?: string;
  tags?: string[];
  createdAt: any;
}

class FeedbackService {
  private appVersion = '1.0.0'; // Should ideally come from a config

  async getAllFeedback(): Promise<FeedbackReport[]> {
    const path = 'feedback_reports';
    try {
      const q = query(collection(db, path), orderBy('createdAt', 'desc'));
      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as FeedbackReport));
    } catch (error) {
      handleFirestoreError(error, OperationType.GET, path);
      return []; // Should not reach here as handleFirestoreError throws
    }
  }

  async updateFeedback(reportId: string, updates: Partial<FeedbackReport>) {
    const path = `feedback_reports/${reportId}`;
    try {
      const docRef = doc(db, 'feedback_reports', reportId);
      await updateDoc(docRef, updates);
    } catch (error) {
      handleFirestoreError(error, OperationType.WRITE, path);
    }
  }

  async deleteFeedback(reportId: string) {
    const path = `feedback_reports/${reportId}`;
    try {
      const { deleteDoc } = await import('firebase/firestore');
      const docRef = doc(db, 'feedback_reports', reportId);
      await deleteDoc(docRef);
    } catch (error) {
      handleFirestoreError(error, OperationType.DELETE, path);
    }
  }

  async captureScreenshot(): Promise<string | null> {
    try {
      console.log('[FeedbackService] captureScreenshot started');
      const root = document.getElementById('root') || document.body;
      if (!root) {
        console.warn('[FeedbackService] No root element found for capture');
        return null;
      }

      // Hide feedback button before capture if needed
      // But we'll handle this by passing a filter to toJpeg
      const options = {
        quality: 0.6, // Lower quality for better reliability and smaller size
        pixelRatio: 1, // Crucial: avoid huge images on High-DPI screens which often crash capture or upload
        cacheBust: true,
        backgroundColor: '#15151e', // Match app theme (board-bg)
        style: {
          'filter': 'none',
          'backdrop-filter': 'none',
          'transform': 'none' // Some transforms break html-to-image
        },
        filter: (node: Node) => {
          // Only process element nodes for filtering
          if (node.nodeType !== 1) return true;
          
          const element = node as HTMLElement;
          
          // Hide the feedback button itself
          if (element.id === 'feedback-button') return false;
          if (element.id === 'feedback-floating-trigger') return false;
          
          // Hide the feedback modal if it's somehow in the root
          // Using getAttribute('class') is the most compatible way to check classes
          try {
            const className = element.getAttribute('class');
            if (className && typeof className === 'string') {
              if (className.includes('feedback-modal')) return false;
              if (className.includes('sonner')) return false; // Hide toasts
              if (className.includes('radix')) return false; // Common for modals/popovers
              if (className.includes('fixed')) {
                // Optionally hide other fixed overlays if they might interfere
              }
            }
          } catch (e) {
            // If attribute access fails, just continue
          }
            
          return true;
        }
      };

      try {
        console.log('[FeedbackService] Calling toJpeg (pixelRatio: 1)...');
        // Give a bit more time for the browser to breathe
        const dataUrl = await toJpeg(root, options);
        console.log('[FeedbackService] Screenshot captured, size (chars):', dataUrl.length);
        return dataUrl;
      } catch (innerError) {
        console.error('[FeedbackService] toJpeg failed, attempting toPng fallback:', innerError);
        try {
          const { toPng } = await import('html-to-image');
          return await toPng(root, { ...options, quality: undefined });
        } catch (pngError) {
          console.error('[FeedbackService] toPng fallback also failed:', pngError);
          return null;
        }
      }
    } catch (error) {
      console.error('[FeedbackService] captureScreenshot total failure:', error);
      return null;
    }
  }

  private getBrowserInfo() {
    const ua = navigator.userAgent;
    let browser = "Unknown";
    if (ua.indexOf("Firefox") > -1) browser = "Firefox";
    else if (ua.indexOf("Chrome") > -1) browser = "Chrome";
    else if (ua.indexOf("Safari") > -1) browser = "Safari";
    else if (ua.indexOf("Edge") > -1) browser = "Edge";
    
    let os = "Unknown";
    if (ua.indexOf("Win") > -1) os = "Windows";
    else if (ua.indexOf("Mac") > -1) os = "MacOS";
    else if (ua.indexOf("X11") > -1) os = "UNIX";
    else if (ua.indexOf("Linux") > -1) os = "Linux";
    else if (ua.indexOf("Android") > -1) os = "Android";
    else if (ua.indexOf("iPhone") > -1) os = "iOS";

    return { browser, os };
  }

  async submitFeedback(
    type: FeedbackType,
    message: string,
    screenshotDataUrl: string | null
  ) {
    console.log('[FeedbackService] submitFeedback called');
    const user = auth.currentUser;
    
    // Allow anonymous feedback as requested
    const userId = user?.uid || `anon_${Math.random().toString(36).substring(2, 9)}`;
    const userName = user?.displayName || 'Usuario Anónimo';
    const userEmail = user?.email || 'anonimo@verbo.app';

    const { browser, os } = this.getBrowserInfo();
    const reportId = Math.random().toString(36).substring(2, 15);
    
    let screenshotUrl = '';
    if (screenshotDataUrl) {
      console.log('[FeedbackService] Processing screenshot...');
      try {
        const storagePath = user ? `feedback/${user.uid}/${reportId}/screenshot.jpg` : `feedback/anonymous/${reportId}/screenshot.jpg`;
        const storageRef = ref(storage, storagePath);
        
        // Add a timeout to the upload
        const uploadPromise = uploadString(storageRef, screenshotDataUrl, 'data_url');
        const timeoutPromise = new Promise((_, reject) => 
          setTimeout(() => reject(new Error('Timeout uploading screenshot')), 15000)
        );

        console.log('[FeedbackService] Uploading screenshot to Storage...');
        await Promise.race([uploadPromise, timeoutPromise]);
        
        console.log('[FeedbackService] Getting download URL...');
        const downloadUrlPromise = getDownloadURL(storageRef);
        const downloadUrlTimeoutPromise = new Promise((_, reject) => 
          setTimeout(() => reject(new Error('Timeout getting download URL')), 10000)
        );
        
        screenshotUrl = await Promise.race([downloadUrlPromise, downloadUrlTimeoutPromise]) as string;
        console.log('[FeedbackService] Screenshot URL obtained:', screenshotUrl);
      } catch (error: any) {
        console.error('[FeedbackService] Error with screenshot:', error);
        // We continue without screenshot if it fails, but log it
        toast.error(`La captura no se pudo subir (${error.message || 'error'}), pero enviaremos el texto.`);
      }
    }

    // Limit message length just in case
    const sanitizedMessage = message.slice(0, 5000);

    console.log('[FeedbackService] Preparing report object...');
    const report: Omit<FeedbackReport, 'createdAt'> & { createdAt: any } = {
      type,
      status: 'new',
      priority: 'normal',
      message: sanitizedMessage,
      screenshotUrl,
      hasScreenshot: !!screenshotUrl,
      route: window.location.pathname,
      userId,
      userName,
      userEmail,
      appVersion: this.appVersion,
      platform: 'web',
      os,
      browser,
      viewportWidth: window.innerWidth,
      viewportHeight: window.innerHeight,
      createdAt: serverTimestamp()
    };

    const path = 'feedback_reports';
    console.log('[FeedbackService] Saving to Firestore:', path, report);
    
    try {
      // Add a timeout to the Firestore write
      const addDocPromise = addDoc(collection(db, path), report);
      const timeoutPromise = new Promise((_, reject) => 
        setTimeout(() => reject(new Error('Timeout saving to Firestore')), 15000)
      );

      const docRef = await Promise.race([addDocPromise, timeoutPromise]) as any;
      console.log('[FeedbackService] Firestore save successful, ID:', docRef.id);
      return docRef.id;
    } catch (error: any) {
      console.error('[FeedbackService] Firestore error:', error);
      handleFirestoreError(error, OperationType.CREATE, path);
      throw error; // Re-throw so the modal can handle it
    }
  }
}

export const feedbackService = new FeedbackService();
